import clsx from 'clsx';
import React, { useEffect, useCallback, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { FilterRendererProps } from '../../DataTable.types';
import { Mixpanel } from '../../../../../utils/mixpanel';
import { useAppSelector } from '../../../../../app/hooks';
import { INVESTOR_ROUTES } from '../../../../../utils/constants/routesConst';
import { DATE_FORMATS } from '../../../../../utils/dateUtils';
import dayjs from 'dayjs';

import Button from '../../../Button/Button';
import CalendarIcon from '../../../../../assets/Svg/CalendarIcon';
import { DatePicker } from '../../../DatePicker/DatePicker';
import closeIcon from '../../../SideDrawer/close.svg';

import styles from './DateRangeFilterRenderer.module.scss';

interface DateRangeValue {
    gte?: number;
    lte?: number;
}

// Utility function to determine page type from current route
const getPageTypeFromRoute = (pathname: string): string => {
    if (pathname.includes(INVESTOR_ROUTES.SCAN)) {
        return 'Leads';
    } else if (pathname.includes(INVESTOR_ROUTES.DEALS)) {
        return 'Deals';
    } else {
        return 'Mandate';
    }
};

// Custom hook for debounced Mixpanel tracking
const useDebouncedMixpanelTracking = (delay: number = 500) => {
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const debouncedTrack = useCallback(
        (eventName: string, eventData: any) => {
            // Clear existing timeout
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            // Set new timeout
            timeoutRef.current = setTimeout(() => {
                Mixpanel.track(eventName, eventData);
            }, delay);
        },
        [delay],
    );

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    return debouncedTrack;
};

export const DateRangeFilterRenderer: React.FC<FilterRendererProps> = ({ column, value, onChange }) => {
    const location = useLocation();
    const userData = useAppSelector((state) => state.user.userData);
    const platformData = useAppSelector((state) => state.user.domainMetadata);
    const debouncedTrack = useDebouncedMixpanelTracking(500);

    const dateRange = useMemo(() => {
        return value?.value ?? ({} as DateRangeValue);
    }, [value]);

    const handleFromDateChange = (date: string) => {
        // Convert ISO date string to epoch time using dayjs
        // Set to the earliest time of the day (00:00:00.000)
        const epochDate = dayjs(date).startOf('day').valueOf();

        const newDateRange = {
            ...dateRange,
            gte: epochDate,
        };

        if (newDateRange.gte || newDateRange.lte) {
            onChange({
                operator: 'BETWEEN',
                value: newDateRange,
                valueType: 'DATE',
            });

            // Track Mixpanel event for date range filter applied (debounced)
            const filterPage = getPageTypeFromRoute(location.pathname);
            const mixpanelEvent = {
                page: filterPage,
                filter_type: 'date_range',
                field: column.field,
                from_date: newDateRange.gte,
                to_date: newDateRange.lte,
                current_url: window.location.href,
                user_email: userData?.primaryEmail || '',
                pno_id: platformData?.investorOrgId || '',
            };
            debouncedTrack('status_timeline_filtered', mixpanelEvent);
        } else {
            onChange([]);
        }
    };

    const handleToDateChange = (date: string) => {
        // Convert ISO date string to epoch time using dayjs
        // Set to the latest time of the day (23:59:59.999)
        const epochDate = dayjs(date).endOf('day').valueOf();

        const newDateRange = {
            ...dateRange,
            lte: epochDate,
        };

        if (newDateRange.gte || newDateRange.lte) {
            onChange({
                operator: 'BETWEEN',
                value: newDateRange,
                valueType: 'DATE',
            });

            // Track Mixpanel event for date range filter applied (debounced)
            const filterPage = getPageTypeFromRoute(location.pathname);
            const mixpanelEvent = {
                page: filterPage,
                filter_type: 'date_range',
                field: column.field,
                from_date: newDateRange.gte,
                to_date: newDateRange.lte,
                current_url: window.location.href,
                user_email: userData?.primaryEmail || '',
                pno_id: platformData?.investorOrgId || '',
            };
            debouncedTrack('status_timeline_filtered', mixpanelEvent);
        } else {
            onChange([]);
        }
    };

    const clearFilters = () => {
        onChange([]);
    };

    const hasActiveFilters = dateRange.gte || dateRange.lte;

    return (
        <div className={clsx(styles.DateRangeFilter, { [styles.FilterApplied]: hasActiveFilters })}>
            <div className={styles.FilterRow}>
                <div className={styles.FilterLabel}>
                    {column?.filterConfig?.label || column.headerLabel || 'Last Status Update (Timeline)'}
                </div>
                <div className={styles.DateInputs}>
                    <div className={styles.DateInput}>
                        <DatePicker
                            value={dateRange.gte}
                            onChange={handleFromDateChange}
                            placeholder="Date From"
                            className={styles.DatePickerInput}
                            unit={<CalendarIcon className={styles.CalendarIcon} />}
                            displayFormat={DATE_FORMATS.ISO}
                            responseFormat={DATE_FORMATS.ISO}
                            unitStyle={{ padding: '0' }}
                            useFixedPosition={true}
                            max={dateRange.lte}
                        />
                    </div>

                    <div className={styles.DateInput}>
                        <DatePicker
                            unitStyle={{ padding: '0' }}
                            value={dateRange.lte}
                            unit={<CalendarIcon className={styles.CalendarIcon} />}
                            min={dateRange.gte}
                            onChange={handleToDateChange}
                            placeholder="Date Till"
                            displayFormat={DATE_FORMATS.ISO}
                            responseFormat={DATE_FORMATS.ISO}
                            className={styles.DatePickerInput}
                            useFixedPosition={true}
                        />
                    </div>
                </div>
                {hasActiveFilters && (
                    <Button
                        text=""
                        icon={closeIcon}
                        type="reset"
                        onClick={clearFilters}
                        variant="white"
                        style={{
                            marginTop: 0,
                            border: '1px solid #E7E7E7',
                            color: '#4D4D4D',
                            fontSize: '0.875rem',
                            padding: '0.5rem 0.75rem',
                            minWidth: 'auto',
                            flexShrink: 0,
                        }}
                    />
                )}
            </div>
        </div>
    );
};
